/**
 * An object that creates a display for the agree/disagree counts on a gallery card.
 */

import i18next from 'i18next';
import { util } from '../../common/utilities.js';

export class ValidationInfoDisplay {
  #aiValidation;
  #userValidation;
  #lockReason = null;
  /** @type {Record<string, {icon: HTMLImageElement, count: HTMLElement}>} Each thumb and its count, by vote. */
  #voteTallies = {};

  /**
   * @param {HTMLElement} container - The DOM element that contains the display.
   * @param {number} agreeCount - The agree count to display.
   * @param {number} disagreeCount - The disagree count to display.
   * @param {string} aiValidation - Either 'Agree' or 'Disagree', showing AI validation if there is any.
   * @param {?string} userValidation - The viewer's own vote on this label, or null if they haven't voted.
   */
  constructor(container, agreeCount, disagreeCount, aiValidation, userValidation) {
    this.agreeCount = agreeCount;
    this.disagreeCount = disagreeCount;
    this.validationContainer = container;
    this.#aiValidation = aiValidation;
    this.#userValidation = userValidation;

    this.#init();
  }

  #init() {
    const container = this.validationContainer;
    const holder = document.createElement('div');
    holder.className = 'validation-info-content';

    // Create outer container for agree and disagree sections.
    this.agreeContainer = document.createElement('div');
    this.agreeContainer.className = 'validation-section-content';
    this.disagreeContainer = document.createElement('div');
    this.disagreeContainer.className = 'validation-section-content';

    // Create the agree and disagree count containers.
    const agreeCountContainer = document.createElement('div');
    const disagreeCountContainer = document.createElement('div');
    agreeCountContainer.className = 'validation-info-count-container';
    disagreeCountContainer.className = 'validation-info-count-container';

    // Build the agree/disagree icons. There is a `-ai` variant of each icon.
    const agreeIcon = this.#makeVoteIcon('Agree');
    const disagreeIcon = this.#makeVoteIcon('Disagree');
    agreeCountContainer.appendChild(agreeIcon);
    disagreeCountContainer.appendChild(disagreeIcon);

    // Create the agree and disagree count text elements.
    this.agreeText = document.createElement('div');
    this.agreeText.className = 'validation-info-count';
    agreeCountContainer.append(this.agreeText);

    this.disagreeText = document.createElement('div');
    this.disagreeText.className = 'validation-info-count';
    disagreeCountContainer.append(this.disagreeText);

    this.updateValCounts(this.agreeCount, this.disagreeCount);
    this.#voteTallies = {
      Agree: { icon: agreeIcon, count: this.agreeText },
      Disagree: { icon: disagreeIcon, count: this.disagreeText },
    };

    this.agreeContainer.append(agreeCountContainer);
    this.disagreeContainer.append(disagreeCountContainer);

    holder.append(this.agreeContainer);
    holder.append(this.disagreeContainer);

    container.append(holder);
  }

  /**
   * Builds an <img> for the agree/disagree vote icon. The icon carries no tooltip of its own; hovering it falls
   * through to the one on its container, so the icon and the count beside it explain the vote the same way.
   * @param {string} action - 'Agree' or 'Disagree'.
   * @returns {HTMLImageElement} The icon, in its outline state.
   */
  #makeVoteIcon(action) {
    const icon = document.createElement('img');
    icon.className = 'validation-info-image';
    icon.src = this.#voteIconSrc(action, false);
    icon.alt = '';
    return icon;
  }

  /**
   * A vote icon's URL, in the requested fill state and with the `-ai` variant when our AI validated this option.
   *
   * Rebuilt from the logical path rather than edited out of the <img>'s current `src`, which carries the outline
   * file's content fingerprint and so can't name the filled one (#5204).
   *
   * @param {string} action - 'Agree' or 'Disagree'.
   * @param {boolean} filled - Whether to use the filled variant rather than the outline one.
   * @returns {string} The icon's URL.
   */
  #voteIconSrc(action, filled) {
    const fill = filled ? 'filled' : 'outline';
    const ai = this.#aiValidation === action ? '-ai' : '';
    return util.assetPath(`images/icons/validation/${action.toLowerCase()}-${fill}${ai}.svg`);
  }

  /**
   * Fills or unfills one thumb's icon — the hover hint that the thumb can be clicked to vote.
   * @param {string} action - 'Agree' or 'Disagree'.
   * @param {boolean} filled - Whether the icon should show its filled variant.
   */
  setVoteIconFilled(action, filled) {
    const container = action === 'Agree' ? this.agreeContainer : this.disagreeContainer;
    const icon = /** @type {?HTMLImageElement} */ (container?.querySelector('.validation-info-image'));
    if (icon) icon.src = this.#voteIconSrc(action, filled);
  }

  /**
   * Ties a vote cast on this card to the count it changed (util.misc.animateVoteChange). The thumb stays outline,
   * since the filled icon matches a voted card's band and would vanish.
   *
   * @param {?string} previous - The viewer's vote before, or null.
   * @param {?string} current - The viewer's vote now, or null once cleared.
   */
  animateVoteChange(previous, current) {
    util.misc.animateVoteChange(this.#voteTallies, previous, current);
  }

  /**
   * Writes each thumb's tooltip, in the same words the Label Detail card uses for the same vote (#4778): what
   * clicking does, how many validators have already voted that way, whether our AI's vote is among them, and — on
   * the option the viewer picked — that clicking again clears it. The two cards show the same counts, so a vote
   * that reads one way on the small card and another way on the expanded one is just two descriptions of one thing.
   *
   * A locked card (the viewer's own label) states that reason on both thumbs instead: none of the rest applies
   * when the thumbs can't be clicked.
   */
  #renderTooltips() {
    const containers = { Agree: this.agreeContainer, Disagree: this.disagreeContainer };
    const counts = { Agree: this.agreeCount, Disagree: this.disagreeCount };
    for (const [action, container] of Object.entries(containers)) {
      const count = counts[action];
      let tip = this.#lockReason;
      if (!tip) {
        const isVoted = this.#userValidation === action;
        if (isVoted) {
          // {{count}} is the *other* validators, so the viewer isn't double-counted in their own tooltip; the
          // i18next `_zero` key covers "nobody else" (see LabelDetail's #renderVoteTooltips for the full note).
          tip = i18next.t(`labelmap:vote-tooltip-voted-${action.toLowerCase()}`, { count: Math.max(0, count - 1) });
        } else {
          tip = i18next.t(`labelmap:vote-tooltip-${action.toLowerCase()}`, { count });
        }
        // Sentences are appended in order of usefulness, so what clicking *does* lands last.
        if (this.#aiValidation === action) tip += ` ${i18next.t('labelmap:vote-tooltip-ai-included')}`;
        if (isVoted) tip += ` ${i18next.t('labelmap:vote-tooltip-clear')}`;
      }
      container.setAttribute('data-ps-tooltip', tip);
    }
  }

  /**
   * Locks both thumbs' tooltips to a single reason, in place of the per-vote text.
   * @param {string} reason - Why validating is blocked on this card.
   */
  setLockReason(reason) {
    this.#lockReason = reason;
    this.#renderTooltips();
  }

  /**
   * @param {number} agreeCount - The agree count to display.
   * @param {number} disagreeCount - The disagree count to display.
   * @param {?string} [userValidation] - The viewer's vote once the change lands; omit to leave it as it was.
   */
  updateValCounts(agreeCount, disagreeCount, userValidation = this.#userValidation) {
    this.agreeCount = agreeCount;
    this.disagreeCount = disagreeCount;
    this.#userValidation = userValidation;
    this.agreeText.innerText = `${this.agreeCount}`;
    this.disagreeText.innerText = `${this.disagreeCount}`;
    this.#renderTooltips();
  }
}
